import { Box, Container, SimpleGrid, Stack, Card, Title, Loader, Text, Center } from "@mantine/core";
import UserProfileCard from "components/UserProfileCard";
import TeamProfileCard from "components/TeamProfileCard";
import TeamStatsSection from "pages/Team/TeamStatsSection";
import UserMatchFeed from "components/profile/UserMatchFeed";
import UserNewFeed from "components/profile/UserNewFeed";
import { useParams } from "react-router-dom";
import { useMeQuery, useUserByIdQuery, useUserTeamsQuery } from "generated/graphql";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { UserAuthData } from "Types/UserDataTypes";

/**
 * UserProfile page
 *
 * Responsive 3-row dashboard:
 * - Row 1: UserProfileCard (left) + Teams list (right) - stacks on mobile
 * - Row 2: User stats (full width)
 * - Row 3: Match feed (left) + News feed (right) - stacks on mobile
 *
 * NOTE: On mobile, content scrolls. On desktop (sm+), uses fixed viewport height.
 *
 * When no userId is provided in URL params, shows the current logged-in user's profile.
 */
export default function UserProfile() {
  const GUTTER = 'md' as const;
  const { userId: paramUserId } = useParams<{ userId: string }>();

  // For self-profile (no userId param), get current user info from auth and Me query
  const authUser = useAuthUser<UserAuthData>();
  const { data: meData, loading: meLoading } = useMeQuery({
    skip: !!paramUserId, // Skip if viewing another user's profile
  });

  // Determine the effective userId - from params or from auth state
  const effectiveUserId = paramUserId ?? authUser?.id ?? meData?.me?.userData?.id;

  // Fetch user details (either self or another user)
  const { data: userData, loading: userLoading, error: userError } = useUserByIdQuery({
    variables: { userId: effectiveUserId! },
    skip: !effectiveUserId,
  });

  // Fetch teams that this user belongs to
  const { data: teamsData, loading: teamsLoading } = useUserTeamsQuery({
    variables: { userId: effectiveUserId! },
    skip: !effectiveUserId,
  });

  const isLoading = meLoading || userLoading;

  if (isLoading) {
    return (
      <Center style={{ height: 'calc(100dvh - 48px)' }}>
        <Loader size="lg" />
      </Center>
    );
  }

  // For self-profile without a userId param, we can fall back to Me query data
  const user = userData?.user ?? (meData?.me?.userData ? {
    id: meData.me.userData.id,
    name: meData.me.userData.name,
    avatarUrl: meData.me.userData.avatarUrl,
    roles: meData.me.userData.roles,
    roleNames: meData.me.userData.roles, // Me query returns roles as roleNames format
  } : null);

  if (userError || !user) {
    return (
      <Center style={{ height: 'calc(100dvh - 48px)' }}>
        <Text c="red">Failed to load user profile.</Text>
      </Center>
    );
  }

  // Map teams from API
  const userTeams = (teamsData?.teams?.items ?? []).map((t) => ({
    id: t.id,
    name: t.name,
    tier: t.tier ?? undefined,
    logoUrl: t.franchise?.logoUrl ?? undefined,
    franchise: t.franchise?.name,
  }));

  // Get the first team for display purposes (if user has multiple teams)
  const primaryTeam = userTeams[0];

  // TODO: Player stats should come from a stats service.
  // Currently showing empty stats until the stats service is rebuilt.
  const playerStats = {
    matchesPlayed: 0,
    wins: 0,
    losses: 0,
    draws: 0,
    roundDiff: 0,
  };

  return (
    <Container fluid p={{ base: "sm", sm: GUTTER }} className="h-auto sm:h-[calc(100dvh-48px)]">
      {/* Mobile: scrollable content; Desktop: fixed viewport height */}
      <div className="flex flex-col gap-3 sm:h-full sm:min-h-0">
        {/* Row 1: User card + Teams list - stacks on mobile */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" className="sm:flex-1 sm:min-h-0">
          <Box className="min-h-[200px] sm:h-full sm:min-h-0">
            <Card withBorder radius="md" p="md" className="h-full flex">
              <UserProfileCard
                avatarUrl={user.avatarUrl ?? undefined}
                name={user.name}
                title="Player"
                team={primaryTeam?.name}
                franchise={primaryTeam?.franchise}
                roles={(user.roleNames ?? []).filter((r): r is string => r !== null)}
                variant="full"
              />
            </Card>
          </Box>

          <Box className="min-h-[250px] sm:h-full sm:min-h-0">
            <Card withBorder radius="md" p="md" className="h-full flex">
              <Stack gap="sm" className="flex-1 min-h-0">
                <Title order={4}>Teams</Title>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {teamsLoading ? (
                    <Center><Loader size="sm" /></Center>
                  ) : userTeams.length === 0 ? (
                    <Text c="dimmed" size="sm">Not currently on any teams.</Text>
                  ) : (
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                      {userTeams.map((t) => (
                        <TeamProfileCard
                          key={t.id}
                          logoUrl={t.logoUrl}
                          teamName={t.name}
                          franchise={t.franchise}
                          tier={t.tier}
                          compact
                        />
                      ))}
                    </SimpleGrid>
                  )}
                </div>
              </Stack>
            </Card>
          </Box>
        </SimpleGrid>

        {/* Row 2: User stats */}
        {/* TODO: Wire up player stats when stats service is available */}
        <Box className="sm:flex-1 sm:min-h-0">
          <TeamStatsSection stats={playerStats} title="Player Stats" fullHeight />
        </Box>

        {/* Row 3: Match feed + News feed - stacks on mobile */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" className="sm:flex-1 sm:min-h-0">
          <Box className="min-h-[300px] sm:h-full sm:min-h-0">
            {/* TODO: Wire up player match history when we have a way to get
                matches for a specific player (not just team matches).
                For now, showing empty state. */}
            <UserMatchFeed
              title="Player Match Feed"
              items={[]}
              fullHeight
              emptyMessage="No match history available."
            />
          </Box>
          <Box className="min-h-[300px] sm:h-full sm:min-h-0">
            {/* TODO: News feed - no news data source exists yet.
                When a news/announcements feature is implemented, wire this up.
                For now, showing empty state. */}
            <UserNewFeed
              title="Player News Feed"
              items={[]}
              fullHeight
            />
          </Box>
        </SimpleGrid>
      </div>
    </Container>
  );
}
